import type { ServerApiClient } from '../../../data/ServerApiClient'
import { trimStr } from './cfoFormat'

/** Rebuild the current URL as the safe return target when Checkout is canceled.
 * Successful Checkout always lands on the backend-owned public confirmation
 * page so a payer does not need an existing FuseGrid session. */
export function cfoBillingCancelUrl(): string {
  const url = new URL(window.location.href)
  url.searchParams.delete('billing')
  url.searchParams.set('billing', 'canceled')
  return url.toString()
}

/** Public post-checkout destination. Stripe substitutes the completed session
 * ID before redirecting, and this route deliberately does not require login. */
export function cfoBillingSuccessUrl(): string {
  return `${window.location.origin}/billing/checkout/complete?session_id={CHECKOUT_SESSION_ID}`
}

/** Start a Stripe-hosted checkout and hand the browser over to it. */
export async function startCfoCheckout(
  appApi: ServerApiClient,
  workbookId: string,
  plan: 'essentials' | 'pro' | 'partner',
  interval: 'monthly' | 'annual' = 'monthly',
): Promise<void> {
  const res = await appApi.billingCheckout({
    workbook_id: workbookId,
    plan,
    interval,
    success_url: cfoBillingSuccessUrl(),
    cancel_url: cfoBillingCancelUrl(),
  })
  const url = trimStr(res?.url)
  if (!url) throw new Error('Checkout could not be started.')
  window.location.assign(url)
}

/** Open the Stripe Billing Portal (card, plan switch, cancel, invoices). */
export async function openCfoBillingPortal(
  appApi: ServerApiClient,
  workbookId: string,
): Promise<void> {
  const url = new URL(window.location.href)
  url.searchParams.delete('billing')
  const res = await appApi.billingPortal({ workbook_id: workbookId, return_url: url.toString() })
  const target = trimStr(res?.url)
  if (!target) throw new Error('Billing portal could not be opened.')
  window.location.assign(target)
}
